import { useAsync } from 'react-use';

import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

export type TeamSubscriptions = {
  /** Subscription ids owned by the team; scopes both resolution and suggestions. */
  teamSubscriptionIds: string[];
  /** Display names for every ingested subscription, keyed by subscription id. */
  namesById: Record<string, string>;
  loading: boolean;
};

const EMPTY = { teamSubscriptionIds: [], namesById: {} };

/**
 * Reads the subscription `Resource` entities ingested from Watchtower. The
 * annotation keys contain dots, which the catalog filter parser mangles, so the
 * team match happens client-side (same approach as `useTeamNames`).
 */
export const useTeamSubscriptions = (teamId?: string): TeamSubscriptions => {
  const catalogApi = useApi(catalogApiRef);

  const { value, loading } = useAsync(async () => {
    const { items } = await catalogApi.getEntities({
      filter: { kind: 'Resource', 'spec.type': 'subscription' },
      fields: ['metadata.name', 'metadata.title', 'metadata.annotations'],
    });

    const wantedTeamId = teamId?.trim().toUpperCase();
    const namesById: Record<string, string> = {};
    const teamSubscriptionIds: string[] = [];

    for (const item of items) {
      const annotations = item.metadata.annotations ?? {};
      const subscriptionId = annotations['tomtom.com/subscription-id'];
      if (!subscriptionId) continue;

      namesById[subscriptionId] =
        annotations['tomtom.com/subscription-name'] ??
        item.metadata.title ??
        subscriptionId;

      const ownerTeamId = (
        annotations['tomtom.com/claimed-team-id'] ||
        annotations['tomtom.com/proposed-team-id'] ||
        ''
      )
        .trim()
        .toUpperCase();

      if (wantedTeamId && ownerTeamId === wantedTeamId) {
        teamSubscriptionIds.push(subscriptionId);
      }
    }

    return { teamSubscriptionIds, namesById };
  }, [catalogApi, teamId]);

  return { ...(value ?? EMPTY), loading };
};
